import * as THREE from 'three';
import type { HatT, UniformT } from '@/content/schema';
import { mat } from './builders';
import { tr } from '@/i18n';

/**
 * Q 版人物（大頭、短身、會擺手擺腳）。取代 V2 像西洋棋的膠囊人。
 * 造型參數集中在這裡；有站立圖（sprite）或 3D 模型（GLB）時由 Stage 換掉。
 */
export const UNIFORM_COLOR: Record<UniformT, string> = {
  white: '#f4f6f6', gray: '#9aa3a8', green: '#8fb573', office: '#6f8fb3', visitor: '#8cc0e8', street: '#e07b5a',
};
export const SKIN = ['#f6d7bd', '#eec29c', '#d9a77c', '#b9825a'];
export interface ChibiOpts {
  uniform: UniformT; hat: HatT; apron?: string; height?: 'short' | 'mid' | 'tall';
  skin?: string; shirt?: string; mask?: boolean; boots?: boolean; face?: 'smile' | 'calm' | 'determined' | 'sleepy';
  hair?: string; name?: string; gloves?: string;
}
export interface ChibiParts { root: THREE.Group; body: THREE.Group; head: THREE.Group; armL: THREE.Group; armR: THREE.Group; legL: THREE.Group; legR: THREE.Group; eyes: THREE.Mesh[]; seed: number }

/**
 * v0.5 帽子規則（廠規）：
 * - 工作服的衣服、褲子、帽子同一個顏色：生產部全白、工務部全灰、倉儲部全草綠；雨鞋全部是白色短筒靴
 * - 帽子頭頂的圈圈 ⭕：黃＝新人（滿三個月就拿掉）、粉＝孕婦、藍＝生產幹部（組長／領班）
 * - 頭的左右兩邊各一組藍色短橫線（一樣長、上下排）：「一」＝課長、「＝」＝副理、「三」＝經理（v0.9.1）
 * - 頭部正中央紅字「廠長」＝廠長
 */
const TOP_RING: Partial<Record<HatT, string>> = { yellow: '#f2c84b', blue: '#3f78d8', pink: '#f4a7c0' };
const SIDE_LINES: Partial<Record<HatT, number>> = { blue1: 1, blue2: 2, blue3: 3 };
let chiefTex: THREE.CanvasTexture | null = null;
function chiefLabel(): THREE.CanvasTexture {
  if (chiefTex) return chiefTex;
  const cv = document.createElement('canvas'); cv.width = 128; cv.height = 64;
  const x = cv.getContext('2d')!;
  x.fillStyle = '#d23c3c'; x.font = 'bold 46px "Noto Sans TC","PingFang TC","Microsoft JhengHei",sans-serif'; x.textAlign = 'center'; x.textBaseline = 'middle';
  x.fillText(tr("廠長"), 64, 34);
  chiefTex = new THREE.CanvasTexture(cv); chiefTex.colorSpace = THREE.SRGBColorSpace;
  return chiefTex;
}

function capsule(r: number, len: number, color: string) {
  const g = new THREE.CapsuleGeometry(r, len, 4, 10);
  return new THREE.Mesh(g, mat(color));
}

export function makeChibi(o: ChibiOpts): THREE.Group {
  const s = o.height === 'short' ? 0.88 : o.height === 'tall' ? 1.1 : 1;
  const root = new THREE.Group();
  const body = new THREE.Group(); root.add(body);
  const skin = o.skin ?? SKIN[0];
  const cloth = o.uniform === 'street' && o.shirt ? o.shirt : UNIFORM_COLOR[o.uniform];
  const working = o.uniform !== 'street' && o.uniform !== 'office';
  // 腳
  const legs: THREE.Group[] = [];
  for (const sx of [-0.13, 0.13]) {
    const lg = new THREE.Group(); lg.position.set(sx, 0.42, 0);
    const leg = capsule(0.085, 0.22, working ? cloth : '#3b4650'); leg.position.y = -0.17; lg.add(leg);
    // 白色短筒雨鞋（工作服）／便鞋（便服、辦公室）
    const boot = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.115, working ? 0.16 : 0.1, 12), mat(working && o.boots !== false ? '#fbfbfb' : '#4b3a2f'));
    boot.position.set(0, working ? -0.33 : -0.36, 0.02); lg.add(boot);
    if (working) { const toe = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 8), mat('#fbfbfb')); toe.scale.set(1, 0.55, 1.3); toe.position.set(0, -0.4, 0.06); lg.add(toe); }
    body.add(lg); legs.push(lg);
  }
  // 身體（圓滾滾）
  const torso = new THREE.Mesh(new THREE.SphereGeometry(0.3, 18, 14), mat(cloth));
  torso.scale.set(1, 1.15, 0.85); torso.position.y = 0.68; body.add(torso);
  if (o.apron) { const ap = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.36, 0.05), mat(o.apron)); ap.position.set(0, 0.62, 0.25); body.add(ap); }
  if (o.uniform === 'visitor') { const clip = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.26, 0.03), mat('#c79c63')); clip.position.set(0.18, 0.62, 0.27); clip.rotation.z = -0.3; body.add(clip); }
  // 手
  const arms: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const ag = new THREE.Group(); ag.position.set(sx * 0.31, 0.86, 0);
    const arm = capsule(0.07, 0.2, cloth); arm.position.y = -0.16; ag.add(arm);
    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.08, 10, 8), mat(working ? (o.gloves ?? '#5aa9e6') : skin)); // 工作時戴 NBR 手套（生食藍、熟食淺藍）
    hand.position.y = -0.33; ag.add(hand);
    ag.rotation.z = sx * 0.12;
    body.add(ag); arms.push(ag);
  }
  // 頭（大）
  // 頭（更大、更圓：v0.4 變可愛）
  const head = new THREE.Group(); head.position.y = 1.2; body.add(head);
  const face = new THREE.Mesh(new THREE.SphereGeometry(0.37, 24, 20), mat(skin)); face.scale.set(1.04, 0.97, 0.95); head.add(face);
  const eyes: THREE.Mesh[] = [];
  const eyeM = mat('#2a2230');
  const eyeH = o.face === 'sleepy' ? 0.03 : 0.085;
  for (const sx of [-0.125, 0.125]) {
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.06, 14, 10), eyeM); e.scale.set(0.9, eyeH / 0.06, 0.6); e.position.set(sx, 0.03, 0.315); head.add(e); eyes.push(e);
    if (o.face !== 'sleepy') {
      const hl = new THREE.Mesh(new THREE.SphereGeometry(0.02, 8, 6), mat('#ffffff')); hl.position.set(sx + 0.02, 0.065, 0.355); head.add(hl);
      const hl2 = new THREE.Mesh(new THREE.SphereGeometry(0.009, 6, 6), mat('#ffffff')); hl2.position.set(sx - 0.018, 0.005, 0.355); head.add(hl2);
    }
    if (o.face === 'determined') { const br = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.016, 0.01), eyeM); br.position.set(sx, 0.13, 0.33); br.rotation.z = sx > 0 ? 0.25 : -0.25; head.add(br); }
  }
  for (const sx of [-0.21, 0.21]) { const ch = new THREE.Mesh(new THREE.CircleGeometry(0.065, 14), new THREE.MeshBasicMaterial({ color: '#ff9a9a', transparent: true, opacity: 0.6 })); ch.position.set(sx, -0.07, 0.325); ch.rotation.y = sx * 0.6; head.add(ch); }
  if (o.mask ?? working) {
    const mk = new THREE.Mesh(new THREE.SphereGeometry(0.22, 14, 10, 0, Math.PI * 2, Math.PI * 0.47, Math.PI * 0.33), mat('#e3eef2'));
    mk.position.set(0, -0.03, 0.13); mk.scale.set(1.15, 1, 1.05); head.add(mk);
  } else {
    const mouth = new THREE.Mesh(new THREE.TorusGeometry(0.045, 0.012, 6, 12, Math.PI), mat('#8a3b3b'));
    mouth.position.set(0, -0.1, 0.3); mouth.rotation.z = o.face === 'determined' ? 0 : Math.PI; head.add(mouth);
  }
  // 髮帽（工作）或頭髮（便服／辦公室）
  if (working) {
    // 髮帽和工作服同色（生產全白、工務全灰、倉儲全草綠）
    const capColor = o.uniform === 'gray' || o.uniform === 'green' ? cloth : '#f7f9f9';
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.39, 22, 12, 0, Math.PI * 2, 0, Math.PI * 0.55), mat(capColor));
    cap.position.y = 0.04; cap.scale.set(1.04, 1.08, 1.04); head.add(cap);
    const puff = new THREE.Mesh(new THREE.SphereGeometry(0.13, 14, 10), mat(capColor)); puff.position.set(0, 0.4, -0.04); puff.scale.set(1.2, 0.7, 1.2); head.add(puff);   // 髮帽頂端的蓬蓬
    const ring = TOP_RING[o.hat];
    if (ring) {   // 頭頂的圈圈 ⭕
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.035, 8, 28), mat(ring));
      band.rotation.x = Math.PI / 2; band.position.set(0, 0.405, -0.03); head.add(band);
    }
    const lines = SIDE_LINES[o.hat] ?? 0;
    for (const sx of [-1, 1]) for (let i = 0; i < lines; i++) {   // 頭的左右兩邊：「一」課長、「＝」副理、「三」經理（每條一樣長）
      const y = 0.11 + ((lines - 1) / 2 - i) * 0.07;
      const hh = (y - 0.04) / 1.08, r = Math.sqrt(Math.max(0, 0.39 * 0.39 - hh * hh)) * 1.04;
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.032, 0.17), mat('#3f78d8'));   // 前後方向的短橫線
      bar.position.set(sx * (r + 0.012), y, 0); head.add(bar);
    }
    if (o.hat === 'chief') {   // 頭部正中央紅字「廠長」
      const tag = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.15), new THREE.MeshBasicMaterial({ map: chiefLabel(), transparent: true }));
      tag.position.set(0, 0.25, 0.345); tag.rotation.x = -0.42; head.add(tag);
    }
  } else {
    const hair = new THREE.Mesh(new THREE.SphereGeometry(0.385, 22, 12, 0, Math.PI * 2, 0, Math.PI * 0.5), mat(o.hair ?? '#2b2220'));
    hair.position.y = 0.03; hair.rotation.x = -0.25; head.add(hair);
    const tuft = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.16, 8), mat(o.hair ?? '#2b2220')); tuft.position.set(0.04, 0.4, 0.02); tuft.rotation.z = -0.5; head.add(tuft);   // 一撮呆毛
  }
  body.scale.setScalar(s);
  const sh = new THREE.Mesh(new THREE.CircleGeometry(0.4, 24), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22, depthWrite: false }));
  sh.rotation.x = -Math.PI / 2; sh.position.y = 0.015; root.add(sh);
  const parts: ChibiParts = { root, body, head, armL: arms[0], armR: arms[1], legL: legs[0], legR: legs[1], eyes, seed: Math.random() * 10 };
  root.userData.chibi = parts;
  root.userData.body = torso;
  return root;
}

export type AnimMode = 'walk' | 'run' | 'carry' | 'idle' | 'busy' | 'wait' | 'seated' | 'push';
/** 每幀呼叫：依模式擺動手腳、呼吸、眨眼 */
export function animateChibi(root: THREE.Object3D, t: number, mode: AnimMode, reduce = false): void {
  const p = root.userData.chibi as ChibiParts | undefined; if (!p) return;
  const tt = t + p.seed;
  if (reduce) { p.armL.rotation.x = p.armR.rotation.x = 0; return; }
  const blink = (tt % 3.7) < 0.12 ? 0.1 : 1; for (const e of p.eyes) e.scale.y = blink * (e.userData.sy ?? (e.userData.sy = e.scale.y));
  p.body.position.y = 0; p.head.rotation.z = 0; p.head.rotation.y = 0; if (mode !== 'run') p.body.rotation.x = 0;
  switch (mode) {
    case 'walk': case 'push': case 'run': case 'carry': {
      const f = mode === 'run' ? 17 : 11, a = mode === 'run' ? 0.95 : 0.6;
      const s = Math.sin(tt * f);
      p.legL.rotation.x = s * a; p.legR.rotation.x = -s * a;
      const hold = mode === 'push' || mode === 'carry';
      p.armL.rotation.x = hold ? -1.25 : -s * (a + 0.1); p.armR.rotation.x = hold ? -1.25 : s * (a + 0.1);
      if (mode === 'run') p.body.rotation.x = 0.18; else p.body.rotation.x = 0;
      p.body.position.y = Math.abs(Math.cos(tt * 11)) * 0.07; p.head.rotation.z = s * 0.08;
      break;
    }
    case 'busy': {
      p.legL.rotation.x = p.legR.rotation.x = 0;
      p.armL.rotation.x = -1.0 + Math.sin(tt * 9) * 0.35; p.armR.rotation.x = -1.0 + Math.sin(tt * 9 + 1.6) * 0.35;
      p.head.rotation.x = 0.25 + Math.sin(tt * 4.5) * 0.04; p.body.position.y = Math.abs(Math.sin(tt * 9)) * 0.015;
      break;
    }
    case 'wait': {
      p.legL.rotation.x = p.legR.rotation.x = 0;
      p.armL.rotation.x = -0.5; p.armR.rotation.x = -0.5; p.armL.rotation.z = 0.9; p.armR.rotation.z = -0.9; // 抱胸
      p.head.rotation.y = Math.sin(tt * 0.7) * 0.5; p.body.position.y = Math.sin(tt * 1.5) * 0.01;
      break;
    }
    case 'seated': {
      p.legL.rotation.x = p.legR.rotation.x = -1.4; p.body.position.y = -0.3;
      p.armL.rotation.x = -1.1 + Math.sin(tt * 14) * 0.08; p.armR.rotation.x = -1.1 + Math.sin(tt * 14 + 2) * 0.08;
      p.head.rotation.x = 0.15;
      break;
    }
    default: {
      p.legL.rotation.x = p.legR.rotation.x = 0;
      p.armL.rotation.x = Math.sin(tt * 1.6) * 0.06; p.armR.rotation.x = -Math.sin(tt * 1.6) * 0.06;
      p.body.position.y = Math.sin(tt * 2) * 0.012; p.head.rotation.y = Math.sin(tt * 0.5) * 0.25;
    }
  }
  if (mode !== 'wait') { p.armL.rotation.z = 0.12 * -1; p.armR.rotation.z = 0.12; }
}
